import { Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { SlaFormField } from '~/app/landing/sla/shared/components/SlaFormField';
import type {
  MaintenanceSlaDraft,
  MaintenanceSlaDraftLevel,
  MaintenanceSlaInterventionMode,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { createFixedLevelsForInterventionMode } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils';

export interface MaintenanceSlaHeaderInterventionModeFieldsProps {
  draft: MaintenanceSlaDraft;
  onChange: (patch: Partial<MaintenanceSlaDraft>) => void;
}

const INTERVENTION_MODE_OPTIONS: Array<{
  value: MaintenanceSlaInterventionMode;
  labelKey: string;
  color: string;
}> = [
  {
    value: 'ONSITE',
    labelKey: 'oems.maintenance.interventionMode.onsite',
    color: 'blue',
  },
  {
    value: 'REMOTE',
    labelKey: 'oems.maintenance.interventionMode.remote',
    color: 'purple',
  },
  {
    value: 'BOTH',
    labelKey: 'oems.maintenance.interventionMode.both',
    color: 'geekblue',
  },
];

function rebuildLevelsForMode(
  currentLevels: MaintenanceSlaDraftLevel[],
  newMode: MaintenanceSlaInterventionMode | null,
): MaintenanceSlaDraftLevel[] {
  const baseLevels = createFixedLevelsForInterventionMode(newMode);

  return baseLevels.map((baseLevel) => {
    const existing = currentLevels.find((l) => l.code === baseLevel.code);
    return existing ?? baseLevel;
  });
}

export function MaintenanceSlaHeaderInterventionModeFields({
  draft,
  onChange,
}: MaintenanceSlaHeaderInterventionModeFieldsProps) {
  const { t } = useTranslation('sla');

  const handleModeChange = (newMode: MaintenanceSlaInterventionMode) => {
    const patch: Partial<MaintenanceSlaDraft> = {
      interventionMode: newMode,
    };

    if (draft.interventionMode === 'BOTH') {
      patch.scopeRules = draft.scopeRules.map((rule) => ({
        ...rule,
        interventionMode: null,
      }));
    }

    patch.levels = rebuildLevelsForMode(draft.levels, newMode);

    onChange(patch);
  };

  const options = INTERVENTION_MODE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: (
      <span>
        <Tag color={opt.color} style={{ marginInlineEnd: 8 }}>
          {opt.value}
        </Tag>
        {t(opt.labelKey)}
      </span>
    ),
  }));

  return (
    <SlaFormField
      label={t('oems.maintenance.fields.interventionMode')}
      required
    >
      <Select
        style={{ width: '100%' }}
        labelInValue={false}
        value={draft.interventionMode ?? 'BOTH'}
        onChange={(value: MaintenanceSlaInterventionMode) =>
          handleModeChange(value)
        }
        options={options}
      />
    </SlaFormField>
  );
}
